import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SimuladorPrejuizos from '../SimuladorPrejuizos';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'simulador-maus-habitos';

/** Página /ferramentas/simulador-maus-habitos */
const PrejuizosPage: React.FC = () => (
  <>
    <Helmet>
      <title>Simulador de Maus Hábitos: Quanto Você Perde de Combustível? – Gas2</title>
      <meta
        name="description"
        content="Pneu murcho, ar-condicionado no máximo, aceleradas bruscas. Selecione seus maus hábitos e descubra quanto dinheiro você joga fora em combustível por ano."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Simulador de Maus Hábitos ao Volante – Gas2" />
      <meta
        property="og:description"
        content="Descubra quanto dinheiro você joga fora em combustível por causa de pneu murcho, excesso de peso e aceleradas bruscas."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Simulador de Maus Hábitos ao Volante – Gas2" />
      <meta
        name="twitter:description"
        content="Calcule o prejuízo anual com pneu murcho, ar-condicionado excessivo e outros maus hábitos de direção."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            Economia de Combustível
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Simulador de Maus Hábitos: Você Sabe Quanto Joga Fora na Gasolina?
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            Pequenos descuidos do dia a dia — pneu com pressão baixa, ar-condicionado
            travado no máximo, porta-malas cheio de coisas inúteis — aumentam o consumo
            do carro em percentuais que parecem pequenos, mas que somados ao longo do ano
            representam centenas de reais jogados fora. Confesse seus "pecados ao volante"
            e veja o impacto real.
          </p>
        </motion.header>

        {/* ── Simulador ── */}
        <SimuladorPrejuizos />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como os Maus Hábitos Aumentam o Consumo?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Cada mau hábito cria uma{' '}
              <strong className="text-slate-200">resistência adicional</strong> que o
              motor precisa vencer, consumindo mais combustível para mover o mesmo peso
              pelo mesmo trajeto. Pneus calibrados abaixo do ideal aumentam o atrito com
              o asfalto. O ar-condicionado aciona um compressor que rouba potência do
              motor. O excesso de peso exige mais força de aceleração e frenagem. Cada
              fator isolado parece trivial — juntos, podem aumentar o gasto em até 35%.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Pneu Murcho Realmente Faz Diferença?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Sim — e mais do que você imagina. Um pneu com{' '}
              <strong className="text-slate-200">25% abaixo da pressão ideal</strong>{' '}
              aumenta o consumo em até 5% e reduz a vida útil do pneu em até 25%. Para
              um carro que roda 1.200 km/mês com gasolina a R$ 5,50 e consumo de 12
              km/l, isso representa quase R$ 35/mês extras — ou R$ 415 por ano — apenas
              por não parar no posto para calibrar.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Quer saber a pressão recomendada para o seu carro? Verifique a etiqueta
              colada na coluna da porta do motorista ou no manual. E use o{' '}
              <a href="/" className="text-amber-400 hover:text-amber-300 underline">
                app Gas2
              </a>{' '}
              para registrar abastecimentos e acompanhar seu consumo real ao longo do
              tempo.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Direção Econômica: O Que Realmente Funciona
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              A técnica mais eficaz é a{' '}
              <strong className="text-slate-200">antecipação de trânsito</strong>: olhar
              longe à frente, soltar o acelerador cedo ao se aproximar de semáforos e
              deixar o carro desacelerar naturalmente (com injeção cortada) em vez de
              frear bruscamente. Essa mudança de hábito, sozinha, pode reduzir o consumo
              urbano em 10–15%.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Consumo e Maus Hábitos
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'Andar na banguela economiza combustível?',
                  a: 'Não. Em carros modernos com injeção eletrônica, o freio motor (em marcha engatada, sem acelerar) corta a injeção de combustível automaticamente. Já em ponto morto (banguela), o motor continua injetando combustível para se manter em marcha lenta.',
                },
                {
                  q: 'Quanto o ar-condicionado consome?',
                  a: 'Em cidade, o ar-condicionado pode aumentar o consumo entre 8% e 15%, dependendo da temperatura externa e da potência do compressor. Na estrada, o impacto é menor pois o motor está mais eficiente. Prefira o modo econômico do ar quando possível.',
                },
                {
                  q: 'Vale a pena usar o GPS para evitar engarrafamentos?',
                  a: 'Sim. Além de economizar tempo, evitar trânsito parado reduz significativamente o consumo. No stop-and-go, o consumo pode ser até 40% pior do que em tráfego fluente a 60 km/h.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default PrejuizosPage;



